import { derive } from '@traits-ts/core'
import { useNotificationCreator } from '../../../composables/useNotificationCreator.js'
import { useMembersStore } from '../../../stores/members.js'
import { useSubKrewesStore } from '../../../stores/subkrewes.js' // Add this
import type { TaskActionButton, TaskListAction } from '../../types/task.js'
import { AsBaseTask } from '../traits/AsBaseTask.js'
import { WithAssignment } from '../traits/WithAssignment.js'
import { WithNotifications } from '../traits/WithNotifications.js'
import { WithReferences } from '../traits/WithReferences.js'
import { WithUIConfiguration } from '../traits/WithUIConfiguration.js'

/**
 * SubKreweJoinRequestTask
 * Represents a request from a member to join a SubKrewe
 */
export class SubKreweJoinRequestTask extends derive(
  AsBaseTask,
  WithAssignment,
  WithReferences,
  WithUIConfiguration,
  WithNotifications,
) {
  /**
   * Create a new join request task
   * @param memberId ID of the member requesting to join
   * @param subkreweId ID of the SubKrewe they want to join
   * @param memberName Optional name of the member
   * @param subkreweName Optional name of the SubKrewe
   */
  constructor(
    memberId: string,
    subkreweId: string,
    memberName: string = '',
    subkreweName: string = '',
  ) {
    super('SubKreweJoinRequestTask')

    // Set basic task properties
    this.title = `Request to join ${subkreweName || 'SubKrewe'} from ${memberName || 'a member'}`
    this.description = `${memberName || 'A member'} has requested to join ${subkreweName || 'this SubKrewe'}.`
    this.taskType = 'subkrewe'
    this.priority = 'medium'

    // Store member and subkrewe data in metadata
    this.metadata = {
      memberId,
      subkreweId,
      memberName,
      subkreweName,
    }

    // Assign to the SubKrewe
    this.assign(subkreweId, 'subkrewe')

    // Add references to both entities
    this.addReference(memberId, 'member', 'applicant')
    this.addReference(subkreweId, 'subkrewe', 'target')

    // Record that we're using these traits
    this.traits = ['WithAssignment', 'WithReferences', 'WithUIConfiguration', 'WithNotifications']
  }

  /**
   * Approve the join request
   * This updates the task status and should trigger adding the member to the SubKrewe
   * @param actingUserId Optional ID of the captain approving the request (for notifications)
   */
  async approve(actingUserId?: string): Promise<boolean> {
    if (this.status === 'completed') {
      return true
    }

    try {
      // Add the member to the SubKrewe first
      if (typeof this.metadata.memberId === 'string' && typeof this.metadata.subkreweId === 'string') {
        const membersStore = useMembersStore()
        const subkrewesStore = useSubKrewesStore()

        // Get the member and subkrewe
        const member = await membersStore.fetchMember(this.metadata.memberId)
        const subkrewe = await subkrewesStore.fetchSubKrewe(this.metadata.subkreweId)

        // Update the member's subkrewe
        if (member && subkrewe) {
          // Pass true to suppress captain notifications since we'll send a dedicated approval notification
          // Pass actingUserId to identify who approved the request
          await subkrewesStore.addMemberToSubKrewe(subkrewe, member, true, actingUserId)
        }
      }

      // Set status to completed
      this.status = 'completed'
      this.completedAt = new Date()
      this.updatedAt = new Date()

      // Add metadata about approval
      this.metadata.approvedAt = new Date()
      this.metadata.approved = true

      // Track who approved the request
      if (actingUserId) {
        this.metadata.approvedById = actingUserId
      }

      // Send notifications when a join request is approved
      if (typeof this.metadata.memberId === 'string') {
        const memberType = typeof this.metadata.memberType === 'string' ? this.metadata.memberType : undefined
        await this.notifyApproved(this.metadata.memberId, this.metadata.subkreweId as string, memberType)
      }

      return true
    }
    catch (error) {
      console.error('Error approving join request:', error)
      return false
    }
  }

  /**
   * Deny the join request
   * This updates the task status and records that the request was denied
   */
  async deny(reason?: string): Promise<boolean> {
    if (this.status === 'canceled') {
      return true
    }

    // Set status to canceled
    this.status = 'canceled'
    this.completedAt = new Date()
    this.updatedAt = new Date()

    // Add metadata about denial
    this.metadata.deniedAt = new Date()
    this.metadata.approved = false

    // Store denial reason if provided
    if (reason) {
      this.metadata.denialReason = reason
    }

    // Send notifications when a join request is denied
    if (typeof this.metadata.memberId === 'string') {
      // Send a special denial notification with more context
      const memberType = typeof this.metadata.memberType === 'string' ? this.metadata.memberType : undefined
      await this.notifyDenied(
        this.metadata.memberId,
        this.metadata.subkreweId as string,
        reason,
        memberType,
      )
    }

    return true
  }

  /**
   * Send a notification that the join request was created
   * This is called when a new application is submitted
   */
  async notifySubmitted(): Promise<string | null> {
    try {
      const subkreweId = this.metadata.subkreweId as string
      const memberId = this.metadata.memberId as string
      const memberName = this.metadata.memberName as string
      const subkreweName = this.metadata.subkreweName as string
      const memberType = typeof this.metadata.memberType === 'string' ? this.metadata.memberType : undefined
      const applicantEmail = typeof this.metadata.applicantEmail === 'string' ? this.metadata.applicantEmail : undefined
      const applicantPhone = typeof this.metadata.applicantPhone === 'string' ? this.metadata.applicantPhone : undefined

      // Get all captains of the SubKrewe
      const captains = await this.getSubKreweCaptains(subkreweId)

      // Send notification to each captain using the dedicated helper
      const notificationCreator = useNotificationCreator()
      const notificationIds = await Promise.all(
        captains.map(captainId =>
          notificationCreator.createNewJoinRequestNotification(
            captainId,
            subkreweId,
            subkreweName,
            memberName,
            memberId,
            memberType,
            applicantEmail,
            applicantPhone,
          ),
        ),
      )
      const notificationId = notificationIds[0] ? notificationIds[0] : null
      // Return the first notification ID (or null if none were sent)
      return notificationIds.length > 0 ? notificationId : null
    }
    catch (error) {
      console.error('Error sending join request notification:', error)
      return null
    }
  }

  /**
   * Send a notification to a captain about a new join request
   */
  private async notifyNewJoinRequest(
    captainId: string,
    subkreweId: string,
    subkreweName: string,
    applicantId: string,
    applicantName: string,
  ): Promise<string | null> {
    try {
      const notificationCreator = useNotificationCreator()

      // Determine redshirt status from task metadata
      const isRedshirt = String(this.metadata.memberType || '').toLowerCase() === 'redshirt'
      const title = `${isRedshirt ? '[Red Shirt] ' : ''}New Join Request`
      const body = `${applicantName}${isRedshirt ? ' (Red Shirt)' : ''} has requested to join your SubKrewe.`

      return await notificationCreator.createMembershipNotification(
        captainId,
        title,
        body,
        subkreweId,
        {
          priority: 'normal',
          sendEmail: true,
          emailTemplate: 'new-join-request',
          metadata: {
            subkreweId,
            subkreweName,
            applicantId,
            applicantName,
            taskId: this.id,
            memberType: isRedshirt ? 'redshirt' : (this.metadata.memberType ?? undefined),
            isRedshirt,
          },
        },
      )
    }
    catch (error) {
      console.error('Error sending join request notification:', error)
      return null
    }
  }

  /**
   * Send a notification that the join request was approved
   */
  private async notifyApproved(
    memberId: string,
    subkreweId: string,
    memberType?: string,
  ): Promise<string | null> {
    try {
      const notificationCreator = useNotificationCreator()

      // Get SubKrewe and captain info for the notification
      const subkreweName = this.metadata.subkreweName as string || 'the SubKrewe'

      // Try to get the name of the captain who approved
      let captainName: string | undefined
      if (this.metadata.approvedById) {
        const memberDetails = await this.getMemberDetails(this.metadata.approvedById as string)
        captainName = memberDetails?.name
      }

      return await notificationCreator.createMembershipApprovedNotification(
        memberId,
        subkreweId,
        subkreweName,
        captainName,
        memberType,
      )
    }
    catch (error) {
      console.error('Error sending approval notification:', error)
      return null
    }
  }

  /**
   * Send a notification that the join request was denied
   */
  private async notifyDenied(
    memberId: string,
    subkreweId: string,
    reason?: string,
    memberType?: string,
  ): Promise<string | null> {
    try {
      const notificationCreator = useNotificationCreator()

      // Get SubKrewe and captain info for the notification
      const subkreweName = this.metadata.subkreweName as string || 'the SubKrewe'

      // Try to get the name of the captain who denied
      let captainName: string | undefined
      if (this.metadata.deniedById) {
        const memberDetails = await this.getMemberDetails(this.metadata.deniedById as string)
        captainName = memberDetails?.name
      }

      return await notificationCreator.createMembershipDeniedNotification(
        memberId,
        subkreweId,
        subkreweName,
        reason,
        captainName,
        memberType,
      )
    }
    catch (error) {
      console.error('Error sending denial notification:', error)
      return null
    }
  }

  /**
  * Cancel the join request
  * Used when a member cancels their own request
  */
  async cancel(reason?: string): Promise<boolean> {
    if (this.status === 'canceled') {
      return true
    }
    // Set status to canceled
    this.status = 'canceled'
    this.completedAt = new Date() // Add this line to set completedAt
    this.updatedAt = new Date()

    // Add metadata about cancellation
    this.metadata.canceledAt = new Date()
    this.metadata.canceledByMember = true

    // Store cancellation reason if provided
    if (reason) {
      this.metadata.cancellationReason = reason
    }

    // Send notification to captains
    if (typeof this.metadata.memberId === 'string' && typeof this.metadata.subkreweId === 'string') {
      await this.notifyCanceled(
        this.metadata.subkreweId,
        this.metadata.memberId,
        reason,
      )
    }

    return true
  }

  /**
 * Send notifications to captains when a member cancels their join request
 */
  private async notifyCanceled(
    subkreweId: string,
    memberId: string,
    reason?: string,
  ): Promise<string | null> {
    try {
      const captains = await this.getSubKreweCaptains(subkreweId)
      if (captains.length === 0) return null

      const notificationCreator = useNotificationCreator()
      const memberName = this.metadata.memberName as string || 'A member'
      const subkreweName = this.metadata.subkreweName as string || 'your SubKrewe'

      // Determine redshirt status from task metadata
      const isRedshirt = String(this.metadata.memberType || '').toLowerCase() === 'redshirt'
      const title = `${isRedshirt ? '[Red Shirt] ' : ''}SubKrewe Join Request Canceled`
      const body = `${memberName}${isRedshirt ? ' (Red Shirt)' : ''} has canceled their request to join ${subkreweName}.${reason ? ` Reason: ${reason}` : ''}`

      // Send notification to each captain
      const notificationIds = await Promise.all(
        captains.map(captainId =>
          notificationCreator.createMembershipNotification(
            captainId,
            title,
            body,
            subkreweId,
            {
              priority: 'normal',
              sendEmail: true,
              emailTemplate: 'join-request-canceled',
              metadata: {
                subkreweId,
                subkreweName,
                applicantId: memberId,
                applicantName: memberName,
                reason,
                memberType: isRedshirt ? 'redshirt' : (this.metadata.memberType ?? undefined),
                isRedshirt,
              },
            },
          ),
        ),
      )

      return notificationIds.length > 0 ? notificationIds[0]! : null
    }
    catch (error) {
      console.error('Error sending cancellation notification:', error)
      return null
    }
  }

  /**
   * Get captains for a SubKrewe
   * @param subkreweId SubKrewe ID
   * @returns Array of captain member IDs
   */
  private async getSubKreweCaptains(subkreweId: string): Promise<string[]> {
    try {
      const subkrewesStore = useSubKrewesStore()
      const subkrewe = await subkrewesStore.fetchSubKrewe(subkreweId)

      if (!subkrewe) {
        return []
      }

      // Extract captain IDs from the captain objects
      const captains = subkrewe.captains || []
      return captains.map(captain =>
        typeof captain === 'string' ? captain : captain.id,
      )
    }
    catch (error) {
      console.error('Error fetching SubKrewe captains:', error)
      return []
    }
  }

  /**
   * Override getActionButtons to customize actions for join requests
   */
  override getActionButtons() {
    const buttons: TaskActionButton[] = []

    // Only show buttons for pending or in-progress requests
    if (this.status === 'pending' || this.status === 'in_progress') {
      buttons.push({
        label: 'Approve',
        icon: 'pi pi-check',
        action: 'approve',
        primary: true,
      })

      buttons.push({
        label: 'Deny',
        icon: 'pi pi-times',
        action: 'deny',
        danger: true,
      })
    }

    return buttons
  }

  /**
   * Override getListActions to provide quick actions in list views
   */
  override getListActions() {
    const actions: TaskListAction[] = []

    // Only show actions for pending or in-progress requests
    if (this.status === 'pending' || this.status === 'in_progress') {
      actions.push({
        label: 'Approve',
        icon: 'pi pi-check',
        action: 'approve',
        primary: true,
      })

      actions.push({
        label: 'Deny',
        icon: 'pi pi-times',
        action: 'deny',
        danger: true,
      })
    }

    return actions
  }

  /**
   * Override getTaskColor to customize the task color
   */
  override getTaskColor() {
    return 'var(--blue-600)' // A distinct color for join requests
  }

  /**
   * Override getTaskIcon to use a specific icon for join requests
   */
  override getTaskIcon() {
    return 'pi pi-user-plus'
  }

  /**
   * Execute the task - for join requests, this doesn't do anything directly
   * as captains need to explicitly approve or deny
   */
  override async execute(): Promise<boolean> {
    // For join requests that are pending, set to in_progress but return false
    // to indicate that execution is not complete
    if (this.status === 'pending') {
      this.status = 'in_progress'
      this.updatedAt = new Date()
      return false // Return false for pending tasks
    }

    // For already completed/canceled tasks, just return true
    return true
  }

  /**
   * Define which metadata fields should be displayed in the UI
   */
  getMetadataDisplayFields() {
    return [
      {
        key: 'referralName',
        label: 'Referral',
        icon: 'pi pi-user-plus',
        condition: task => !!task.metadata.referralName,
      },
      {
        key: 'applicationMessage',
        label: 'Message from Applicant',
        icon: 'pi pi-comment',
        condition: task => !!task.metadata.applicationMessage,
      },
      {
        key: 'cancellationReason',
        label: 'Cancellation Reason',
        icon: 'pi pi-times-circle',
        condition: task => !!task.metadata.cancellationReason && task.status === 'canceled',
      },
      {
        key: 'denialReason',
        label: 'Denial Reason',
        icon: 'pi pi-times-circle',
        condition: task => !!task.metadata.denialReason && task.status === 'canceled',
      },
    ]
  }
}
